import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { api, useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import ContractReminderModal from './ContractReminderModal';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import NotificationCenter from './NotificationCenter';
import ExportButton from './ExportButton';
import {
  Building2,
  LayoutDashboard,
  Ticket,
  Hotel,
  Users,
  MapPin,
  Wrench,
  LogOut,
  Menu,
  X,
  ChevronDown,
  User,
  UserCircle,
  Calendar,
  Brain,
  Activity,
  BookOpen,
  BarChart3,
  FileCheck,
  Mail,
  ClipboardList,
  Megaphone,
  Eye,
  Lock,
  AlertOctagon,
  Zap,
  Sun,
  Moon,
  Siren,
  ShieldAlert,
  TrendingUp,
  Sparkles,
  Boxes,
  Award,
  Inbox
} from 'lucide-react';

const roleLabels = {
  super_admin: 'Super Admin',
  hotel_manager: 'Hotel Manager',
  capo_manutentore: 'Capo Manutentore',
  technician: 'Tecnico',
  housekeeping: 'Housekeeping',
  frontoffice: 'Front Office',
  sede: 'Sede'
};

const Layout = () => {
  const { user, logout } = useAuth();
  const { theme, cycleTheme } = useTheme();

  // Icona del toggle: mostra il tema SUCCESSIVO che si attiverà al click.
  // Ciclo: light → dark → data-intel → light
  const themeToggleInfo = (() => {
    if (theme === 'light') return { Icon: Moon, label: 'Passa al tema scuro', accent: false };
    if (theme === 'dark') return { Icon: Zap, label: 'Passa al tema Data Intel', accent: true };
    return { Icon: Sun, label: 'Passa al tema chiaro', accent: false };
  })();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeBlockages, setActiveBlockages] = useState(0);
  const [onlineCount, setOnlineCount] = useState(0);

  // Poll attivo blocchi ascensori — solo per ruoli che vedono la voce
  useEffect(() => {
    const ALLOWED = ['super_admin', 'hotel_manager', 'frontoffice', 'capo_manutentore', 'sede'];
    if (!user?.role || !ALLOWED.includes(user.role)) return;

    let cancelled = false;
    const fetchActive = async () => {
      try {
        const res = await api.get('/elevator-blockages?status=in_blocco&limit=50');
        if (!cancelled) setActiveBlockages(Array.isArray(res.data) ? res.data.length : 0);
      } catch (_) {
        if (!cancelled) setActiveBlockages(0);
      }
    };
    fetchActive();
    const interval = setInterval(fetchActive, 60000); // refresh ogni 60s
    return () => { cancelled = true; clearInterval(interval); };
  }, [user?.role]);

  // Poll utenti online — solo super_admin (live count nella sidebar)
  useEffect(() => {
    if (user?.role !== 'super_admin') return;
    let cancelled = false;
    const fetchOnline = async () => {
      try {
        const res = await api.get('/presence/online');
        if (!cancelled) setOnlineCount(res.data?.count || 0);
      } catch (_) {
        if (!cancelled) setOnlineCount(0);
      }
    };
    fetchOnline();
    const t = setInterval(fetchOnline, 30000); // refresh ogni 30s
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.role]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getNavItems = () => {
    const items = [];
    
    if (user?.role === 'super_admin') {
      items.push(
        { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard Globale' },
        { to: '/emergenze', icon: Siren, label: 'Rubrica Emergenze', danger: true },
        { to: '/ascensori', icon: AlertOctagon, label: 'Ascensori', pulseDot: true },
        { to: '/tickets', icon: Ticket, label: 'Tutti i Ticket' },
        { to: '/scheduled', icon: Calendar, label: 'Manutenzioni Programmate' },
        { to: '/heatmap', icon: Activity, label: 'Heatmap Problemi' },
        { to: '/intel', icon: Sparkles, label: 'Intel & Analytics' },
        { to: '/disciplina', icon: ShieldAlert, label: 'Disciplina Tecnici' },
        { to: '/consumi', icon: Zap, label: 'Consumi Hotel' },
        { to: '/produttivita', icon: BarChart3, label: 'Produttivit\u00e0 Tecnici' },
        { to: '/qualita-ticket', icon: Award, label: 'Qualit\u00e0 Ticket' },
        { to: '/richieste', icon: Inbox, label: 'Inbox Richieste' },
        { to: '/contratti-manutenzione', icon: FileCheck, label: 'Manutenzioni Contrattuali' },
        { to: '/analytics', icon: MapPin, label: 'Analisi' },
        { to: '/bacheca', icon: ClipboardList, label: 'Bacheca' },
        { to: '/novita', icon: Megaphone, label: 'Novit\u00e0' },
        { to: '/manuale', icon: BookOpen, label: 'Manuale Utente' },
        // Divider — voci che seguono sono protette da password
        { divider: true, label: 'Area Riservata' },
        { to: '/hotels', icon: Hotel, label: 'Hotel', sensitive: true },
        { to: '/users', icon: Users, label: 'Utenti', sensitive: true },
        { to: '/impostazioni-notifiche', icon: Mail, label: 'Notifiche Email', sensitive: true },
        { to: '/system-health', icon: Activity, label: 'Stato Sistema', sensitive: true },
        { to: '/strumenti-dati', icon: Wrench, label: 'Strumenti Dati', sensitive: true },
        { to: '/utenti-online', icon: Eye, label: 'Utenti Online', sensitive: true, onlineBadge: true },
        { to: '/geolocalizzazione', icon: MapPin, label: 'Geolocalizzazione', sensitive: true }
      );
    } else if (user?.role === 'hotel_manager') {
      items.push(
        { to: '/hotel', icon: LayoutDashboard, label: 'Dashboard Hotel' },
        { to: '/emergenze', icon: Siren, label: 'Rubrica Emergenze', danger: true },
        { to: '/ascensori', icon: AlertOctagon, label: 'Ascensori', pulseDot: true },
        { to: '/tickets', icon: Ticket, label: 'Ticket' },
        { to: '/scheduled', icon: Calendar, label: 'Manutenzioni Programmate' },
        { to: '/heatmap', icon: Activity, label: 'Heatmap Problemi' },
        { to: '/intel', icon: Sparkles, label: 'Intel & Analytics' },
        { to: '/disciplina', icon: ShieldAlert, label: 'Disciplina Tecnici' },
        { to: '/consumi', icon: Zap, label: 'Consumi Hotel' },
        { to: '/produttivita', icon: BarChart3, label: 'Produttivit\u00e0 Tecnici' },
        { to: '/qualita-ticket', icon: Award, label: 'Qualit\u00e0 Ticket' },
        { to: '/richieste', icon: Inbox, label: 'Inbox Richieste' },
        { to: '/contratti-manutenzione', icon: FileCheck, label: 'Manutenzioni Contrattuali' },
        { to: '/analytics', icon: MapPin, label: 'Analisi' },
        { to: '/bacheca', icon: ClipboardList, label: 'Bacheca' },
        { to: '/novita', icon: Megaphone, label: 'Novit\u00e0' },
        { to: '/manuale', icon: BookOpen, label: 'Manuale Utente' }
      );
    } else if (user?.role === 'capo_manutentore') {
      items.push(
        { to: '/tickets', icon: Ticket, label: 'Tutti i Ticket' },
        { to: '/emergenze', icon: Siren, label: 'Rubrica Emergenze', danger: true },
        { to: '/ascensori', icon: AlertOctagon, label: 'Ascensori', pulseDot: true },
        { to: '/scheduled', icon: Calendar, label: 'Manutenzioni Programmate' },
        { to: '/heatmap', icon: Activity, label: 'Heatmap Problemi' },
        { to: '/qualita-ticket', icon: Award, label: 'Qualit\u00e0 Ticket' },
        { to: '/richieste', icon: Inbox, label: 'Inbox Richieste' },
        { to: '/bacheca', icon: ClipboardList, label: 'Bacheca' },
        { to: '/novita', icon: Megaphone, label: 'Novit\u00e0' },
        { to: '/manuale', icon: BookOpen, label: 'Manuale' }
      );
    } else if (user?.role === 'technician') {
      items.push(
        { to: '/tecnico', icon: Wrench, label: 'I Miei Lavori' },
        { to: '/mia-performance', icon: TrendingUp, label: 'La Mia Performance' },
        { to: '/emergenze', icon: Siren, label: 'Rubrica Emergenze', danger: true },
        { to: '/bacheca', icon: ClipboardList, label: 'Bacheca' },
        { to: '/novita', icon: Megaphone, label: 'Novit\u00e0' },
        { to: '/manuale', icon: BookOpen, label: 'Manuale' }
      );
    } else if (user?.role === 'housekeeping') {
      items.push(
        { to: '/housekeeping', icon: Building2, label: 'Segnalazione Rapida' },
        { to: '/emergenze', icon: Siren, label: 'Rubrica Emergenze', danger: true },
        { to: '/bacheca', icon: ClipboardList, label: 'Bacheca' },
        { to: '/novita', icon: Megaphone, label: 'Novit\u00e0' },
        { to: '/manuale', icon: BookOpen, label: 'Manuale' }
      );
    } else if (user?.role === 'frontoffice') {
      items.push(
        { to: '/frontoffice', icon: UserCircle, label: 'Dashboard' },
        { to: '/emergenze', icon: Siren, label: 'Rubrica Emergenze', danger: true },
        { to: '/ascensori', icon: AlertOctagon, label: 'Ascensori', pulseDot: true },
        { to: '/tickets', icon: Ticket, label: 'Ticket' },
        { to: '/qualita-ticket', icon: Award, label: 'La Mia Qualit\u00e0' },
        { to: '/bacheca', icon: ClipboardList, label: 'Bacheca' },
        { to: '/novita', icon: Megaphone, label: 'Novit\u00e0' },
        { to: '/manuale', icon: BookOpen, label: 'Manuale' }
      );
    } else if (user?.role === 'sede') {
      items.push(
        { to: '/richieste', icon: Inbox, label: 'Inbox Richieste' },
        { to: '/emergenze', icon: Siren, label: 'Rubrica Emergenze', danger: true },
        { to: '/ascensori', icon: AlertOctagon, label: 'Ascensori', pulseDot: true },
        { to: '/consumi', icon: Zap, label: 'Consumi Hotel' },
        { to: '/contratti-manutenzione', icon: FileCheck, label: 'Manutenzioni Contrattuali' },
        { to: '/bacheca', icon: ClipboardList, label: 'Bacheca' },
        { to: '/novita', icon: Megaphone, label: 'Novit\u00e0' },
        { to: '/manuale', icon: BookOpen, label: 'Manuale' }
      );
    }
    
    return items;
  };

  const navItems = getNavItems();

  return (
    <div className="h-screen bg-[#f8fafc] dark:bg-[#060B16] overflow-hidden transition-colors" data-testid="main-layout">
      {/* Mobile header */}
      <header className="hostfix-mobile-header lg:hidden fixed top-0 left-0 right-0 h-14 z-50 px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white hover:bg-white/10"
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <div className="flex items-center gap-2">
            <img src="/hostfix-logo-icon-small.png" alt="HostFix" className="w-8 h-8 object-contain rounded-md" />
            <span className="font-bold text-white text-sm">HostFix</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={cycleTheme}
            aria-label={themeToggleInfo.label}
            title={themeToggleInfo.label}
            className="w-9 h-9 rounded-lg flex items-center justify-center text-white hover:bg-white/10 transition-colors"
            data-testid="theme-toggle-mobile"
          >
            <themeToggleInfo.Icon className={`w-4 h-4 ${themeToggleInfo.accent ? 'text-blue-300' : ''}`} />
          </button>
          <NotificationCenter />
        </div>
      </header>

      {/* Sidebar - Dark Navy */}
      <aside
        className={`hostfix-sidebar fixed top-0 left-0 h-full w-[260px] z-40 transition-transform duration-300 lg:translate-x-0 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-16 px-5 flex items-center gap-3 border-b border-white/10">
          <img src="/hostfix-logo-icon-small.png" alt="HostFix" className="w-10 h-10 object-contain rounded-md" />
          <div>
            <h1 className="font-bold text-white text-sm tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>HostFix</h1>
            <p className="text-[10px] text-white/40 tracking-wide uppercase">Hotel Maintenance IA</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {navItems.map((item, idx) => {
            // Divider con label per separare sezione "Area Riservata"
            if (item.divider) {
              return (
                <div
                  key={`divider-${idx}`}
                  className="px-3 pt-5 pb-2 select-none"
                  data-testid="sidebar-divider-restricted"
                >
                  <div className="flex items-center gap-2">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/20 to-white/10" />
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] font-semibold text-white/40">
                      <Lock className="w-3 h-3" />
                      {item.label}
                    </div>
                    <div className="h-px flex-1 bg-gradient-to-l from-transparent via-white/20 to-white/10" />
                  </div>
                </div>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''} ${item.sensitive ? 'sidebar-nav-item-sensitive' : ''} ${item.danger ? 'sidebar-nav-item-red' : ''} ${item.desktopOnly ? '!hidden lg:!flex' : ''}`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-[18px] h-[18px]" />
                <span className="truncate">{item.label}</span>
                {item.sensitive && !item.pulseDot && !(item.onlineBadge && onlineCount > 0) && (
                  <Lock className="ml-auto w-3 h-3 text-white/30" />
                )}
                {item.pulseDot && activeBlockages > 0 && (
                  <span
                    className="ml-auto flex items-center gap-1.5"
                    title={`${activeBlockages} ascensor${activeBlockages === 1 ? 'e' : 'i'} in blocco`}
                    data-testid="elevator-active-blockages-dot"
                  >
                    <span className="elevator-pulse-dot" />
                    <span className="text-xs text-red-300 font-semibold tabular-nums">{activeBlockages}</span>
                  </span>
                )}
                {item.onlineBadge && onlineCount > 0 && (
                  <span
                    className="ml-auto flex items-center gap-1.5"
                    title={`${onlineCount} utent${onlineCount === 1 ? 'e' : 'i'} online`}
                    data-testid="online-users-badge"
                  >
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    <span className="text-xs text-emerald-300 font-semibold tabular-nums">{onlineCount}</span>
                  </span>
                )}
                {item.badge && (
                  <span
                    className="ml-auto px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-sm"
                    data-testid={`nav-badge-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* PTK Credits */}
        <div className="px-4 py-2 border-t border-white/5">
          <p className="text-[10px] text-white/30 text-center tracking-wide">
            Developed by <span className="font-semibold text-white/50">PTK</span>
          </p>
        </div>

        {/* User section */}
        <div className="p-3 border-t border-white/10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-between px-3 py-2.5 h-auto hover:bg-white/8 text-white"
                data-testid="user-menu-trigger"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/15 rounded-lg flex items-center justify-center">
                    <User className="w-4 h-4 text-white/80" />
                  </div>
                  <div className="text-left">
                    <p className="text-xs font-medium text-white/90">{user?.name}</p>
                    <p className="text-[10px] text-white/40">{roleLabels[user?.role]}</p>
                  </div>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-white/40" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Il mio account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-slate-600 cursor-pointer" onClick={() => navigate('/profilo')} data-testid="profile-link">
                <User className="w-4 h-4 mr-2" />
                Profilo
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-600"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-[260px] pt-14 lg:pt-0 h-screen overflow-y-auto">
        {/* Desktop header */}
        <header className="hidden lg:flex h-14 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200/60 dark:border-slate-800 px-8 items-center justify-between sticky top-0 z-20">
          <div>
            <Badge variant="secondary" className="bg-slate-100 text-slate-600 font-medium text-xs">
              {roleLabels[user?.role]}
            </Badge>
          </div>
          
          <div className="flex items-center gap-3">
            {['super_admin', 'hotel_manager'].includes(user?.role) && (
              <ExportButton />
            )}
            <NotificationCenter />
            <button
              type="button"
              onClick={cycleTheme}
              aria-label={themeToggleInfo.label}
              title={themeToggleInfo.label}
              className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all hover:bg-slate-100 dark:hover:bg-slate-800 ${
                themeToggleInfo.accent ? 'text-blue-500' : 'text-slate-600 dark:text-slate-300'
              }`}
              data-testid="theme-toggle"
            >
              <themeToggleInfo.Icon
                className="w-4 h-4"
                style={themeToggleInfo.accent ? { filter: 'drop-shadow(0 0 6px rgba(59,130,246,0.55))' } : undefined}
              />
            </button>
            <div className="w-px h-6 bg-slate-200 dark:bg-slate-700" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 h-9" data-testid="desktop-user-menu">
                  <div className="w-7 h-7 bg-[#1e3a5f] rounded-lg flex items-center justify-center">
                    <User className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="text-sm font-medium text-slate-700">{user?.name}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Il mio account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/profilo')}>
                  <User className="w-4 h-4 mr-2" />
                  Profilo
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-600"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <div className={`p-6 lg:p-8 ${
          ['technician', 'housekeeping', 'frontoffice'].includes(user?.role) ? 'pb-24 md:pb-8' : ''
        }`}>
          <Outlet />
        </div>
      </main>
      <ContractReminderModal />
    </div>
  );
};

export default Layout;
